import type {
  SupplierDistributionEstimateParametersType,
  SupplierDistributionPreviewResponseType,
} from "@sellar-io/api-v2/types";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import {
  apiV2AssertResponse,
  type ApiV2QueryOptions,
  apiV2Request,
  getDefaultQueryOptionsV2,
} from "../apiV2Request";

const quarterHourMs = 15 * 60 * 1000;
const currentBucket = () =>
  Math.ceil(Date.now() / quarterHourMs) * quarterHourMs;

export const useSupplierDistributionPreview = (
  params: SupplierDistributionEstimateParametersType | null,
  options?: ApiV2QueryOptions<SupplierDistributionPreviewResponseType["data"]>,
) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  const [bucket, setBucket] = useState(currentBucket);
  useEffect(() => {
    const timer = window.setInterval(() => setBucket(currentBucket()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return useQuery({
    gcTime: 60 * 60 * 1000,
    queryKey: [
      user?.uid,
      business?.id,
      "supplier-distribution-preview",
      params,
      bucket,
    ],
    queryFn: () =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: "supplier-distribution-preview/estimates",
        params: params ?? {},
      }).then(
        (response) =>
          apiV2AssertResponse<SupplierDistributionPreviewResponseType["data"]>(
            response,
          ).data,
      ),
    ...getDefaultQueryOptionsV2<
      SupplierDistributionPreviewResponseType["data"]
    >({
      context: { scope: "supplier", user, business },
      options: {
        staleTime: quarterHourMs,
        ...options,
        enabled: params !== null && options?.enabled !== false,
      },
    }),
  });
};
